/**
 * Beispieldaten der Oberflächen-Shell. Ersetzt später die API-Antworten;
 * die Formen orientieren sich bereits am Datenmodell (owner-scoped je Vermieter).
 */

import type { Cent, ISODate, Schluessel } from '@nkp/shared';

export interface DemoObjekt {
  id: string;
  bezeichnung: string;
  adresse: string;
  einheiten: number;
  vermietet: number;
  wohnflaecheQm: number;
  co2Kennwert: number;
  laufendeAbrechnung: string;
}

export interface DemoAbrechnung {
  id: string;
  objekt: string;
  von: ISODate;
  bis: ISODate;
  status: 'entwurf' | 'festgeschrieben' | 'versendet';
  saldoCent: Cent;
  einheiten: number;
}

export interface DemoBeleg {
  id: string;
  datum: ISODate;
  lieferant: string;
  kostenartCode: string;
  betragCent: Cent;
  schluessel: Schluessel;
  umlagefaehig: boolean;
}

export interface DemoMietverhaeltnis {
  id: string;
  mietpartei: string;
  objekt: string;
  einheit: string;
  seit: ISODate;
  bis: ISODate | null;
  personen: number;
  vorauszahlungCent: Cent;
  offeneForderungenCent: Cent;
}

export const DEMO_OBJEKTE: DemoObjekt[] = [
  {
    id: 'obj-1',
    bezeichnung: 'Lindenstraße 14',
    adresse: '45127 Essen',
    einheiten: 6,
    vermietet: 5,
    wohnflaecheQm: 412,
    co2Kennwert: 28,
    laufendeAbrechnung: '2025',
  },
  {
    id: 'obj-2',
    bezeichnung: 'Am Wasserturm 3',
    adresse: '44135 Dortmund',
    einheiten: 3,
    vermietet: 1,
    wohnflaecheQm: 214,
    co2Kennwert: 19,
    laufendeAbrechnung: '2025',
  },
];

export const DEMO_ABRECHNUNGEN: DemoAbrechnung[] = [
  {
    id: 'abr-1',
    objekt: 'Lindenstraße 14',
    von: '2025-01-01',
    bis: '2025-12-31',
    status: 'entwurf',
    saldoCent: 41280,
    einheiten: 6,
  },
  {
    id: 'abr-2',
    objekt: 'Am Wasserturm 3',
    von: '2024-01-01',
    bis: '2024-12-31',
    status: 'versendet',
    saldoCent: -12450,
    einheiten: 3,
  },
  {
    id: 'abr-3',
    objekt: 'Lindenstraße 14',
    von: '2024-01-01',
    bis: '2024-12-31',
    status: 'festgeschrieben',
    saldoCent: 87310,
    einheiten: 6,
  },
];

export const DEMO_BELEGE: DemoBeleg[] = [
  {
    id: 'bel-1',
    datum: '2025-02-11',
    lieferant: 'Stadt Essen, Kasse',
    kostenartCode: 'grundsteuer',
    betragCent: 86000,
    schluessel: 'wohnflaeche',
    umlagefaehig: true,
  },
  {
    id: 'bel-2',
    datum: '2025-03-04',
    lieferant: 'Gebäudedienste Kern GmbH',
    kostenartCode: 'gebaeudereinigung',
    betragCent: 124000,
    schluessel: 'wohnflaeche',
    umlagefaehig: true,
  },
  {
    id: 'bel-3',
    datum: '2025-05-22',
    lieferant: 'Stadtwerke Essen',
    kostenartCode: 'wasser',
    betragCent: 213540,
    schluessel: 'verbrauch',
    umlagefaehig: true,
  },
  {
    id: 'bel-4',
    datum: '2025-06-08',
    lieferant: 'Sanitär Ohm GmbH',
    kostenartCode: 'sonstige',
    betragCent: 49800,
    schluessel: 'wohnflaeche',
    umlagefaehig: false,
  },
];

export const DEMO_MIETVERHAELTNISSE: DemoMietverhaeltnis[] = [
  {
    id: 'mv-1',
    mietpartei: 'Berger, Katrin',
    objekt: 'Lindenstraße 14',
    einheit: 'Wohnung 1, EG links',
    seit: '2021-04-01',
    bis: null,
    personen: 2,
    vorauszahlungCent: 17000,
    offeneForderungenCent: 0,
  },
  {
    id: 'mv-2',
    mietpartei: 'Yildiz, Murat',
    objekt: 'Lindenstraße 14',
    einheit: 'Wohnung 2, OG rechts',
    seit: '2023-09-15',
    bis: null,
    personen: 3,
    vorauszahlungCent: 19500,
    offeneForderungenCent: 24080,
  },
  {
    id: 'mv-3',
    mietpartei: 'Hoffmann, Lena',
    objekt: 'Am Wasserturm 3',
    einheit: 'Wohnung 3, DG',
    seit: '2019-01-01',
    bis: '2025-08-31',
    personen: 1,
    vorauszahlungCent: 14500,
    offeneForderungenCent: 0,
  },
];

export const STATUS_BEZEICHNUNG: Record<DemoAbrechnung['status'], string> = {
  entwurf: 'Entwurf',
  festgeschrieben: 'Festgeschrieben',
  versendet: 'Versendet',
};

export type Zaehlermedium = 'kaltwasser' | 'warmwasser' | 'waerme' | 'hkv' | 'strom' | 'gas';

export interface DemoZaehler {
  id: string;
  objekt: string;
  nummer: string;
  medium: Zaehlermedium;
  einheit: string;
  fernablesbar: boolean;
  letzterStand: number | null;
  letztesDatum: ISODate | null;
}

export const DEMO_ZAEHLER: DemoZaehler[] = [
  {
    id: 'z-1',
    objekt: 'Lindenstraße 14',
    nummer: 'WMZ-EG-01',
    medium: 'waerme',
    einheit: 'kWh',
    fernablesbar: true,
    letzterStand: 14820,
    letztesDatum: '2025-12-31',
  },
  {
    id: 'z-2',
    objekt: 'Lindenstraße 14',
    nummer: 'KW-EG-01',
    medium: 'kaltwasser',
    einheit: 'm³',
    fernablesbar: false,
    letzterStand: 412,
    letztesDatum: '2025-12-31',
  },
  {
    id: 'z-3',
    objekt: 'Lindenstraße 14',
    nummer: 'WW-EG-01',
    medium: 'warmwasser',
    einheit: 'm³',
    fernablesbar: false,
    letzterStand: 138,
    letztesDatum: '2025-12-31',
  },
  {
    id: 'z-4',
    objekt: 'Am Wasserturm 3',
    nummer: 'STR-ALLG',
    medium: 'strom',
    einheit: 'kWh',
    fernablesbar: false,
    letzterStand: 3260,
    letztesDatum: '2025-12-31',
  },
];

export type Ampel = 'rot' | 'gelb' | 'gruen';

export interface DemoPflicht {
  id: string;
  objekt: string;
  anlage: string;
  bezeichnung: string;
  rechtsgrundlage: string;
  verbindlichkeit: 'gesetz' | 'regel_der_technik' | 'vertraglich' | 'empfehlung';
  naechsteFaellig: ISODate | null;
  tageBisFaellig: number | null;
  ampel: Ampel;
}

export interface DemoStichtag {
  bezeichnung: string;
  katalogBezeichnung: string;
  stichtag: ISODate;
  folge: string;
  tageBis: number;
}

/** Fällige und überfällige Prüf-/Wartungspflichten (Anlagenbuch). */
export const DEMO_PFLICHTEN: DemoPflicht[] = [
  {
    id: 'pf-1',
    objekt: 'Lindenstraße 14',
    anlage: 'Zentraler Warmwasserspeicher',
    bezeichnung: 'Legionellenuntersuchung Trinkwasser',
    rechtsgrundlage: '§ 31 TrinkwV 2023',
    verbindlichkeit: 'gesetz',
    naechsteFaellig: '2026-07-30',
    tageBisFaellig: -46,
    ampel: 'rot',
  },
  {
    id: 'pf-2',
    objekt: 'Lindenstraße 14',
    anlage: 'Personenaufzug',
    bezeichnung: 'Aufzugsprüfung (ZÜS)',
    rechtsgrundlage: '§ 16 BetrSichV',
    verbindlichkeit: 'gesetz',
    naechsteFaellig: '2026-10-15',
    tageBisFaellig: 31,
    ampel: 'gelb',
  },
  {
    id: 'pf-3',
    objekt: 'Am Wasserturm 3',
    anlage: 'Gasbrennwertkessel',
    bezeichnung: 'Kehrung und Überprüfung',
    rechtsgrundlage: 'KÜO',
    verbindlichkeit: 'gesetz',
    naechsteFaellig: '2026-11-02',
    tageBisFaellig: 49,
    ampel: 'gelb',
  },
  {
    id: 'pf-4',
    objekt: 'Am Wasserturm 3',
    anlage: 'Blitzschutzanlage',
    bezeichnung: 'Prüfung Blitzschutz',
    rechtsgrundlage: 'DIN EN 62305-3',
    verbindlichkeit: 'regel_der_technik',
    naechsteFaellig: '2026-08-01',
    tageBisFaellig: -44,
    ampel: 'rot',
  },
];

export interface DemoAnlage {
  id: string;
  objekt: string;
  bezeichnung: string;
  gewerk: string;
  typ: string;
  hersteller: string | null;
  baujahr: number | null;
  standort: string | null;
  /** Bezeichnungen der zugeordneten Prüfpflichten (Verweis auf DEMO_PFLICHTEN). */
  pflichten: string[];
}

/** Anlagen des Anlagenbuchs. Die Pflichten entstehen automatisch aus dem Prüfkatalog. */
export const DEMO_ANLAGEN: DemoAnlage[] = [
  {
    id: 'an-1',
    objekt: 'Lindenstraße 14',
    bezeichnung: 'Zentraler Warmwasserspeicher',
    gewerk: 'Trinkwasser',
    typ: 'Großanlage > 400 l',
    hersteller: 'Viessmann',
    baujahr: 2016,
    standort: 'Keller, Technikraum',
    pflichten: ['Legionellenuntersuchung Trinkwasser'],
  },
  {
    id: 'an-2',
    objekt: 'Lindenstraße 14',
    bezeichnung: 'Personenaufzug',
    gewerk: 'Aufzug',
    typ: 'Seilaufzug',
    hersteller: 'Schindler',
    baujahr: 2012,
    standort: 'Treppenhaus',
    pflichten: ['Aufzugsprüfung (ZÜS)'],
  },
  {
    id: 'an-3',
    objekt: 'Am Wasserturm 3',
    bezeichnung: 'Gasbrennwertkessel',
    gewerk: 'Heizung',
    typ: 'Gas-Brennwert 24 kW',
    hersteller: 'Vaillant',
    baujahr: 2019,
    standort: 'Heizraum',
    pflichten: ['Kehrung und Überprüfung'],
  },
  {
    id: 'an-4',
    objekt: 'Am Wasserturm 3',
    bezeichnung: 'Blitzschutzanlage',
    gewerk: 'Blitzschutz',
    typ: 'Äußerer Blitzschutz Klasse III',
    hersteller: null,
    baujahr: 2008,
    standort: 'Dach',
    pflichten: ['Prüfung Blitzschutz'],
  },
];

/** Harte gesetzliche Stichtage, die die eigenen Anlagen betreffen. */
export const DEMO_STICHTAGE: DemoStichtag[] = [
  {
    bezeichnung: 'Bleigrenzwert sinkt auf 5 µg/l',
    katalogBezeichnung: 'Bleileitungen entfernen oder stilllegen',
    stichtag: '2028-01-12',
    folge: 'Verschärfter Grenzwert nach § 17 Abs. 6 TrinkwV',
    tageBis: 485,
  },
  {
    bezeichnung: 'Heizungsprüfung Altanlagen (Einbau vor 01.10.2009)',
    katalogBezeichnung: 'Heizungsprüfung und Optimierung',
    stichtag: '2027-09-30',
    folge: 'Spätester Prüftermin nach § 60b GModG',
    tageBis: 381,
  },
];
